import { Box, Button, CircularProgress, Divider, Grid, LinearProgress, Paper, Typography } from '@material-ui/core';
import { ArrowBack, People, Save } from '@material-ui/icons';
import moment from 'moment';
import PropTypes from 'prop-types';
import React, { useCallback, useMemo, useState } from 'react';
import {
  getDuplicateCandidateListURL,
  getIncomingLienertApplicantUserByIdURL,
  updateCandidateURL,
  updateIncomingLienertApplicantUserURL
} from '../constants';
import GenericFormPopUp from '../generic_components/GenericFormPopUp';
import DuplicateCandidateSelector from './DuplicateCandidateSelector';
import ProjectMatchingForm from '../projectArchive/ProjectMatchingForm';
import ProjectMatchingMotivationTable from '../projectArchive/ProjectMatchingMotivationTable';
import { post } from '../utils/apiUtils';
import { formatPhoneNumber, getURLParam } from '../utils/utilFunctions';
import useApiQuery from './hooks/useApiQuery';
import TEXT from './text';

/**
 * @typedef Props
 * @property {object} history - React Routers history object
 */

/**
 *
 * @param {Props} param0
 * @returns
 */
const isNotEmpty = val => val !== undefined && val !== null && val !== '';

const IncomingLienertApplicantDetail = ({ history, handleSnackbarOpen }) => {
  const id = useMemo(() => getURLParam(history, 'id'), [history]);
  const [openDialog, setOpenDialog] = React.useState(false);
  const [openCandidateSelector, setOpenCandidateSelector] = React.useState(false);
  const [selectedCandidate, setSelectedCandidate] = React.useState(null);
  const [isUpdating, setIsUpdating] = useState(false);
  const { data, isLoading, isSuccess, isFailed, refetch } = useApiQuery(() => post(getIncomingLienertApplicantUserByIdURL, { id }), {
    enabledOnInit: !!id
  });

  const { data: foundCandidates, isLoading: foundCandidatesLoading, refetch: foundCandidatesRefetch } = useApiQuery(
    () =>
      post(getDuplicateCandidateListURL, {
        firstName: data.firstName,
        lastName: data.lastName,
        dateOfBirth: data.birthDate,
        mail: data.mail
      }),
    { enabledOnInit: !!data }
  );

  const remapDataObject = d => {
    const result = {};

    if (isNotEmpty(d.salutation)) result.title = d.salutation;
    if (isNotEmpty(d.mailAddress)) result.mail = d.mailAddress;
    if (isNotEmpty(d.phoneNumberMobile)) result.phoneNumber = d.phoneNumberMobile;
    if (isNotEmpty(d.dateOfBirth)) result.birthDate = d.dateOfBirth;
    if (isNotEmpty(d['links.linkedIn'])) result.linkedIn = d['links.linkedIn'];

    const otherKeys = ['dateOfBirth', 'firstName', 'lastName', 'street', 'postalCode', 'city'];
    otherKeys.forEach(k => {
      if (isNotEmpty(d[k])) result[k] = d[k];
    });

    return result;
  };

  const updateAPICall = async dataObject => {
    setIsUpdating(true);
    const dataObjectRemapped = remapDataObject(dataObject);
    const cleanedData = Object.fromEntries(Object.entries(dataObject).filter(([, v]) => v !== ''));

    try {
      try {
        await post(updateIncomingLienertApplicantUserURL, {
          incomingApplicantId: id,
          candidateId: selectedCandidate.id,
          fields: dataObjectRemapped
        });
      } catch (err) {
        console.error(err);
      }

      try {
        const response = await post(updateCandidateURL, {
          candidateId: selectedCandidate.id,
          updateData: cleanedData,
          ignoreGeoLocation: true
        });

        if (response.statusMessage === 'update_success') {
          handleSnackbarOpen('Die Stammdaten wurden erfolgreich übernommen.');
        } else if (response.statusMessage === 'no_valid_postal_code_for_selected_country') {
          handleSnackbarOpen('Die eingegebene Postleitzahl existiert nicht im Land des Kandidaten.');
        } else if (response.statusMessage === 'no_country_selected') {
          handleSnackbarOpen('Kein Land beim Kandidaten hinterlegt. Adresse kann nicht übernommen werden.');
        } else if (response.statusMessage === 'no_valid_postal_code') {
          handleSnackbarOpen('Die eingegebene Postleitzahl existiert nicht.');
        } else {
          handleSnackbarOpen('Fehler beim Aktualisieren des Kandidaten.');
        }
      } catch (err) {
        console.error(err);
      }
    } finally {
      setIsUpdating(false);
      foundCandidatesRefetch();
      refetch();
    }
  };

  const handleOnClickToCandidateButton = useCallback(() => {
    history.push(`/candidates?id=${data.candidateId || data.matchingProfile.candidateId}`);
  }, [history, data]);

  const handleDialog = useCallback(() => {
    setOpenDialog(prev => !prev);
    if (openDialog) {
      setSelectedCandidate(null);
    }
  }, [openDialog]);

  const handleTransferButtonClick = useCallback(() => {
    setOpenCandidateSelector(true);
  }, []);

  const handleCandidateSelect = useCallback(candidate => {
    setSelectedCandidate(candidate);
    setOpenCandidateSelector(false);
    setOpenDialog(true);
  }, []);

  const handleCandidateSelectorCancel = useCallback(() => {
    setOpenCandidateSelector(false);
  }, []);

  const handleOpenPdfInNewTab = useCallback(
    doc => {
      if (doc.contentUrl) {
        window.open(doc.contentUrl, '_blank');
      } else {
        console.error('No contentUrl available for document:', doc.fileName);
        handleSnackbarOpen('Fehler beim Laden des Dokuments');
      }
    },
    [handleSnackbarOpen]
  );

  if (!data) {
    return null;
  }

  if (foundCandidatesLoading || isUpdating) {
    return (
      <div id="loading-indicator-overlay">
        <div id="loading-indicator-container">
          <CircularProgress className="mb-3" size={60} thickness={5} />
        </div>
      </div>
    );
  }

  const forms = {
    formFields: {
      salutation: {
        value: data.title,
        label: 'Anrede',
        type: 'dropDown',
        content: ['Herr', 'Frau'],
        customClass: 'col-6 mb-2'
      },
      dateOfBirth: {
        value: data.birthDate,
        label: 'Geburtstag',
        type: 'date',
        customClass: 'col-6 mb-2'
      },
      firstName: {
        value: data.firstName,
        label: 'Vorname',
        customClass: 'col-6 mb-2'
      },
      lastName: {
        value: data.lastName,
        label: 'Nachname',
        customClass: 'col-6 mb-2'
      },
      street: {
        value: data.street,
        label: 'Straße',
        customClass: 'col-12 mb-2'
      },
      postalCode: {
        value: data.postalCode,
        label: 'PLZ',
        customClass: 'col-6 mb-2'
      },
      city: {
        value: data.city,
        label: 'Ort',
        customClass: 'col-6 mb-2'
      },
      phoneNumberMobile: {
        value: data.phoneNumber,
        label: 'Mobil',
        customClass: 'col-6 mb-2'
      },
      mailAddress: {
        value: data.mail,
        label: 'E-Mail',
        customClass: 'col-6 mb-2',
        disabled: true
      },
      'links.linkedIn': {
        value: data.linkedIn,
        label: 'LinkedIn',
        customClass: 'col-6 mb-2'
      }
    }
  };

  return (
    <div className="container pt-4">
      <Button startIcon={<ArrowBack />} onClick={() => history.goBack()} variant="outlined">
        {TEXT.lienertApplicantDetails.actions.backButton}
      </Button>
      <div className="pt-4">
        <Paper style={{ padding: '50px', marginBottom: '10px' }}>
          {isFailed && <Typography color="error">{TEXT.lienertApplicantDetails.error.loadingError}</Typography>}
          {isLoading && <LinearProgress />}
          {isSuccess && (
            <Grid container spacing={2}>
              <Grid item xs={4}>
                <Box display="flex" flexDirection="column" style={{ gap: '20px' }}>
                  <Box marginBottom={4}>
                    <Typography variant="subtitle1">{TEXT.lienertApplicantDetails.title.headerData}</Typography>
                    <Divider />
                  </Box>
                  <Box display="flex" justifyContent="space-between">
                    <Typography variant="caption">{TEXT.lienertApplicantDetails.title.salutation}</Typography>
                    <Typography variant="subtitle2">{data.title}</Typography>
                  </Box>
                  <Box display="flex" justifyContent="space-between">
                    <Typography variant="caption">{TEXT.lienertApplicantDetails.title.firstName}</Typography>
                    <Typography variant="subtitle2">{data.firstName}</Typography>
                  </Box>
                  <Box display="flex" justifyContent="space-between">
                    <Typography variant="caption">{TEXT.lienertApplicantDetails.title.lastName}</Typography>
                    <Typography variant="subtitle2">{data.lastName}</Typography>
                  </Box>
                  <Box display="flex" justifyContent="space-between">
                    <Typography variant="caption">{TEXT.lienertApplicantDetails.title.birthDate}</Typography>
                    <Typography variant="subtitle2">{moment.utc(data.birthDate).format('DD.MM.YYYY')}</Typography>
                  </Box>
                  <Box display="flex" justifyContent="space-between">
                    <Typography variant="caption">{TEXT.lienertApplicantDetails.title.email}</Typography>
                    <Typography variant="subtitle2">{data.mail}</Typography>
                  </Box>
                  <Box display="flex" justifyContent="space-between">
                    <Typography variant="caption">{TEXT.lienertApplicantDetails.title.phoneNumber}</Typography>
                    <Typography variant="subtitle2">{formatPhoneNumber(data.phoneNumber)}</Typography>
                  </Box>
                  <Box display="flex" justifyContent="space-between">
                    <Typography variant="caption">{TEXT.lienertApplicantDetails.title.city}</Typography>
                    <Typography variant="subtitle2">{data.city}</Typography>
                  </Box>
                  <Box display="flex" justifyContent="space-between">
                    <Typography variant="caption">{TEXT.lienertApplicantDetails.title.postalCode}</Typography>
                    <Typography variant="subtitle2">{data.postalCode}</Typography>
                  </Box>
                  <Box display="flex" justifyContent="space-between">
                    <Typography variant="caption">{TEXT.lienertApplicantDetails.title.street}</Typography>
                    <Typography variant="subtitle2">{data.street}</Typography>
                  </Box>
                  <Box display="flex" justifyContent="space-between">
                    <Typography variant="caption">{TEXT.lienertApplicantDetails.title.location}</Typography>
                    <Typography variant="subtitle2">
                      {data.location ? (data.location[0] + data.location.slice(1).toLowerCase()).replace(/ue/g, 'ü') : ''}
                    </Typography>
                  </Box>
                  <Box display="flex" justifyContent="space-between">
                    <Typography variant="caption">{TEXT.lienertApplicantDetails.title.linkedIn}</Typography>

                    {data.linkedIn && (
                      <a href={data.linkedIn} target="_blank" rel="noopener noreferrer" variant="subtitle2">
                        {TEXT.lienertApplicantDetails.actions.toLinkedIn}
                      </a>
                    )}
                  </Box>
                  {data.matchingProfile && data.matchingProfile.uploadedDocuments && data.matchingProfile.uploadedDocuments.length > 0 && (
                    <>
                      {data.matchingProfile.uploadedDocuments.map(doc => {
                        const fileName = doc.fileName || '';
                        const fileNameLower = fileName.toLowerCase();
                        const isCV = fileNameLower.includes('_cv.pdf');
                        const isReference = fileNameLower.includes('_zeugnisse.pdf');
                        const isDiploma = fileNameLower.includes('_diplome.pdf');
                        let label = TEXT.lienertApplicantDetails.documentTypes.unknown;
                        if (isCV) {
                          label = TEXT.lienertApplicantDetails.documentTypes.cv;
                        } else if (isReference) {
                          label = TEXT.lienertApplicantDetails.documentTypes.reference;
                        } else if (isDiploma) {
                          label = TEXT.lienertApplicantDetails.documentTypes.diploma;
                        }
                        return (
                          <Box key={doc.fileName} display="flex" justifyContent="space-between">
                            <Typography variant="caption" className="mr-4">
                              {label}
                            </Typography>
                            <Typography
                              variant="subtitle2"
                              onClick={() => handleOpenPdfInNewTab(doc)}
                              style={{ cursor: 'pointer', textDecoration: 'underline', wordBreak: 'break-all' }}
                            >
                              {doc.fileName}
                            </Typography>
                          </Box>
                        );
                      })}
                    </>
                  )}
                </Box>
                <Divider style={{ marginTop: '10px', marginBottom: '10px' }} />
                <Box display="flex" flexDirection="column" style={{ gap: '20px', marginTop: '10px' }}>
                  {(data.candidateId || data.matchingProfile.candidateId) && (
                    <Button onClick={handleOnClickToCandidateButton} variant="outlined" startIcon={<People />}>
                      {TEXT.lienertApplicantDetails.actions.toCandidateButton}
                    </Button>
                  )}
                  {foundCandidates.length > 0 && foundCandidates[0].id && (
                    <Button onClick={handleTransferButtonClick} variant="outlined" startIcon={<Save />}>
                      {TEXT.lienertApplicantDetails.actions.applyDataToCandidateButton}
                    </Button>
                  )}
                </Box>
              </Grid>
              <DuplicateCandidateSelector
                open={openCandidateSelector}
                candidates={foundCandidates}
                onSelect={handleCandidateSelect}
                onCancel={handleCandidateSelectorCancel}
              />
              {selectedCandidate && (
                <GenericFormPopUp
                  open={openDialog}
                  headlineText="Stammdaten & Suchprofil übernehmen"
                  genericFormProps={{
                    formFields: forms.formFields,
                    isCreate: true,
                    handleSubmit: updateAPICall,
                    handleClose: handleDialog
                  }}
                />
              )}
              <Grid item xs={8}>
                <Box marginBottom={4}>
                  <Typography variant="subtitle1">{TEXT.lienertApplicantDetails.title.headerMatchingProfile}</Typography>
                  <Divider />
                </Box>
                {isSuccess && data.matchingProfile ? (
                  <Box marginTop="-48px">
                    <ProjectMatchingForm
                      key={`${id}-matching`}
                      selectedProjectId="noProject"
                      data={data.matchingProfile}
                      handleSnackbarOpen={() => {}}
                      inCandiateArchive
                    />
                    {data.matchingProfile.motivation && Object.keys(data.matchingProfile.motivation).length > 0 && (
                      <ProjectMatchingMotivationTable key={`${id}-matchingTable`} motivationData={data.matchingProfile.motivation} />
                    )}
                  </Box>
                ) : (
                  <Typography>{TEXT.lienertApplicantDetails.error.noData}</Typography>
                )}
              </Grid>
            </Grid>
          )}
        </Paper>
      </div>
    </div>
  );
};

IncomingLienertApplicantDetail.propTypes = {
  history: PropTypes.object.isRequired,
  handleSnackbarOpen: PropTypes.func.isRequired
};

export default IncomingLienertApplicantDetail;
